import { IconFilter16 } from '@warp-ds/icons/react/filter-16';
import { IconSorting16 } from '@warp-ds/icons/react/sorting-16';
import { Button } from '@warp-ds/react/components/button';
import { Modal } from '@warp-ds/react/components/modal';
import { Select } from '@warp-ds/react/components/select';
import { Toggle, type ToggleEntry } from '@warp-ds/react/components/toggle';
import { type ChangeEvent, useState } from 'react';

import { SortDetails } from 'shared/page/SortDetails';
import { thousandSeparated } from 'shared/util/formatting';
import { Breadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import type { SortValue } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const sorting: Record<string, string> = displayValues.sorting;

type LmsTopBarProps = {
    numberOfOffers?: number;
    filtersOpen: boolean;
    toggleFilters: (value: boolean) => void;
    sortOptions?: SortValue[];
    defaultSort: string;
    selectedSort?: string;
    changeSort: (value?: string) => void;
};

export const LmsTopBar = ({
    numberOfOffers,
    filtersOpen,
    toggleFilters,
    sortOptions,
    defaultSort,
    selectedSort,
    changeSort,
}: LmsTopBarProps) => {
    const [sortModalOpen, setSortModalOpen] = useState(false);

    const currentSort = selectedSort ?? defaultSort;

    const sortToggleOptions: ToggleEntry[] =
        sortOptions?.map((option) => ({
            label: sorting[option],
            value: option,
        })) ?? [];

    const selectedSortToggle: ToggleEntry[] = sorting[currentSort] ? [{ label: sorting[currentSort], value: currentSort }] : [];

    const onSortDropdownChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        changeSort(value === defaultSort ? undefined : value);
    };

    const onSortToggleChange = (entry: ToggleEntry): void => {
        changeSort(entry.value === defaultSort ? undefined : (entry.value as string));
        setSortModalOpen(false);
    };

    return (
        <>
            <section
                data-testid="resultTopBar"
                className="fixed top-[50px] left-0 right-0 z-10 py-16 px-16 s-bg border-b md:relative md:grid md:grid-cols-3 md:gap-32 md:px-0 md:inset-auto! md:border-b-0">
                <div className="hidden md:block md:col-span-1">
                    <Breadcrumbs vertical="lms" />
                </div>
                <div className="flex justify-between items-center gap-8 md:col-span-2">
                    <h2 className="sr-only">Resultatinnstillinger</h2>
                    <Button
                        primary={filtersOpen}
                        utility={!filtersOpen}
                        small
                        onClick={() => toggleFilters(!filtersOpen)}
                        aria-label={filtersOpen ? 'Vis resultat' : 'Vis filter'}
                        className="flex items-center gap-8 md:hidden"
                        data-testid="showFilterButton">
                        <IconFilter16 aria-hidden />
                        {filtersOpen ? 'Resultat' : 'Filter'}
                    </Button>
                    <p className="text-center text-s md:text-m m-0" data-testid="resultCount">
                        <span className="font-bold">{numberOfOffers ? thousandSeparated(numberOfOffers) : '0'}</span> treff
                    </p>
                    <SortDetails section="pakkereiser" className="hidden md:flex" />
                    <Button
                        utility
                        small
                        onClick={() => setSortModalOpen(true)}
                        className="flex items-center gap-8 md:hidden"
                        data-testid="sortButton"
                        aria-haspopup="dialog"
                        aria-expanded={sortModalOpen}>
                        <IconSorting16 aria-hidden />
                        {sorting[currentSort]}
                    </Button>
                    <Select
                        className="hidden md:block"
                        aria-label="Sorter på"
                        value={currentSort}
                        onChange={onSortDropdownChange}
                        data-testid="sortDropdown">
                        {sortOptions?.map((option) => (
                            <option key={option} value={option}>
                                {sorting[option]}
                            </option>
                        ))}
                    </Select>
                </div>
            </section>
            <Modal open={sortModalOpen} onDismiss={() => setSortModalOpen(false)} title="Sorter etter" right>
                <Toggle
                    type="radio"
                    options={sortToggleOptions}
                    selected={selectedSortToggle}
                    onChange={onSortToggleChange}
                    className="flex flex-col gap-8"
                />
            </Modal>
        </>
    );
};
